import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Company, FeatureUser, IUser } from '@tacliatech/types';
import { StorageService, UserService } from '@web-frontend/shared/services';
import { CompanyModuleService } from '@web-frontend/shared/services/company';
import { Subscription } from 'rxjs';

class Permission {
  key: string;
  keyName: string;
  value: string;
  checked: boolean;
}

class PermissionsInternal {
  type: string;
  expanded = false;
  permissions?: Permission[];
}

class TablePermissions {
  permissionsKeys: string[];
  show: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
  approve: boolean;
}

@Component({
  selector: 'roma-manage-permission',
  templateUrl: './manage-permission.component.html',
  styleUrls: ['./manage-permission.component.scss'],
})
export class ManagePermissionComponent implements OnInit, OnDestroy {
  constructor(
    private userService: UserService,
    private companyModuleService: CompanyModuleService
  ) {}
  private sub$ = new Subscription();
  user: IUser;
  permissions: PermissionsInternal[] = [];

  @Input()
  userId: string;

  @Input()
  autoUpdate = true;

  class = 'custom-properties-container col-12 col-xl-5';
  @Input() set adjust(value: boolean) {
    if (value) {
      this.class = 'custom-properties-container ';
    }
  }

  @Output()
  change = new EventEmitter<string[]>();

  tablePermissions: TablePermissions[] = [];
  loading = false;

  modules: Company.Module[];

  ngOnInit(): void {
    this.orderPermissionsMethod();

    this.getInternalPermissions();

    if (this.userId) {
      this.getUser();
    }

    this.sub$.add(
      this.companyModuleService.activeModules$.subscribe((modules) => {
        this.modules = modules;
      })
    );
  }

  ngOnDestroy(): void {
    this.sub$.unsubscribe();
  }

  orderPermissionsMethod() {
    const perm: TablePermissions = {
      permissionsKeys: [
        'Functionalitie',
        'CompanyAdjust',
        'User',
        'Catalogue',
        'Final',
        'InternalVendor',
        'ClockHour',
        'ClockHourAbsence',
        'ClockHourCompany',
        'ClockHourAbsenceCompany',
        'Bill',
        'Quote',
        'Proform',
        'Waybills',
        'Expense',
        'Sale',
        'Report',
        'Calendar',
        'Deal',
        'OnlineProfile',
        'Project',
        'Activity',
        'Payment',
        'Equipment',
        'Address',
      ],
      show: true,
      create: true,
      update: true,
      delete: true,
      approve: true,
    };
    this.tablePermissions.push(perm);
  }

  getInternalPermissions() {
    this.loading = true;
    const prefixes = [
      'p_clockhourcompany_',
      'p_clockhourabsencecompany_',
      'p_geolocation_',
      'p_user_',
      'p_comapanyadjust_',
    ];

    // Filter out features ending with 'List'
    const features = Object.keys(FeatureUser).filter(
      (item) => !item.endsWith('List')
    );

    this.permissions = features.map((featureKey) => {
      const permission: PermissionsInternal = {
        type: featureKey,
        expanded: false,
        permissions: [],
      };

      const featureItemsKeys = Object.keys(FeatureUser[featureKey]);
      permission.permissions = featureItemsKeys.map((itemKey) => {
        const value = FeatureUser[featureKey][itemKey];
        return {
          key: itemKey,
          keyName: `permissions.keys.${itemKey}`,
          value,
          checked: !prefixes.some((prefix) => value.startsWith(prefix)),
        };
      });

      return permission;
    });

    this.loading = false;
    if (!this.userId) {
      this.change.emit(this.parseActualPermissions());
    }
  }

  getUser() {
    this.loading = true;
    this.sub$.add(
      this.userService.findById(this.userId).subscribe((res) => {
        this.user = res;
        this.refactorInternalPermissions();
        this.loading = false;
      })
    );
  }

  refactorInternalPermissions() {
    if (Array.isArray(this.user?.permissions)) {
      const userPermsSet = new Set(this.user.permissions);
      this.permissions.forEach((permGroup) => {
        permGroup.permissions?.forEach((perm) => {
          perm.checked = userPermsSet.has(perm.value);
        });
      });
    }
  }

  updateUserPermissions() {
    if (this.userId) {
      this.sub$.add(
        this.userService
          .update(this.userId, {
            company: StorageService.CompanyId,
            permissions: this.parseActualPermissions(),
          })
          .subscribe()
      );
    }
  }

  forbiddenPermissions = ['p_user_create'];
  filterForbiddenPermissions(permissions: string[]): string[] {
    return permissions.filter(
      (perm) => !this.forbiddenPermissions.includes(perm)
    );
  }

  parseActualPermissions(): string[] {
    const perms = [];
    this.permissions.forEach((permission) => {
      permission.permissions.forEach((item) => {
        if (item.checked) {
          perms.push(item.value);
        }
      });
    });
    return this.filterForbiddenPermissions(perms);
  }

  existsPermission(source: string, key: string): boolean {
    const permGroup = this.permissions.find((it) => it.type === key);
    if (!permGroup || !permGroup.permissions) return false;

    const permValue = FeatureUser[key][source];
    if (this.forbiddenPermissions.includes(permValue)) {
      return false;
    }

    return permGroup.permissions.some((it) => it.value === permValue);
  }

  getPermissionByType(source: string, key: string) {
    const index = this.permissions.findIndex((it) => it.type == key);

    return this.permissions[index].permissions.find(
      (it) => it.value == FeatureUser[key][source]
    );
  }

  onClickCheckbox(source: string, key: string) {
    const permGroup = this.permissions.find((it) => it.type === key);
    if (!permGroup) return;

    const permList = permGroup.permissions;
    const permValue = FeatureUser[key][source];
    const perm = permList?.find((it) => it.value === permValue);
    if (!perm) return;

    if (source === 'show' && perm.checked) {
      ['create', 'update', 'delete', 'approve'].forEach((feature) => {
        const featureValue = FeatureUser[key][feature];
        const featurePerm = permList.find((it) => it.value === featureValue);
        if (featurePerm) featurePerm.checked = false;
      });
    }

    perm.checked = !perm.checked;

    if (source !== 'show' && perm.checked) {
      const showValue = FeatureUser[key]['show'];
      const showPerm = permList.find((it) => it.value === showValue);
      if (showPerm) showPerm.checked = true;
    }

    if (this.userId && this.autoUpdate) {
      this.updateUserPermissions();
    } else {
      this.change.emit(this.parseActualPermissions());
    }
  }
}
